var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { createContext, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState, } from 'react';
import classNames from 'classnames';
import { getUuid } from 'ziphy-web-shared/basic/helpers';
import { useGetDropdownPosition } from 'ziphy-web-shared/basic/hooks';
import { ClickOutside } from 'ziphy-web-shared/basic/lib/utilities';
import styles from './Dropdown.module.scss';
export const DropdownContext = createContext(undefined);
export const Dropdown = forwardRef((_a, forwardedRef) => {
    var { id: _id, renderButton, onClickOutside, onOpen, onAfterClose, maxWidth = 480, maxHeight = 630, resizeDeps, direction, className, classNamePopup, children } = _a, rest = __rest(_a, ["id", "renderButton", "onClickOutside", "onOpen", "onAfterClose", "maxWidth", "maxHeight", "resizeDeps", "direction", "className", "classNamePopup", "children"]);
    // todo: move to useDropdown
    const [isOpen, setIsOpen] = useState(null);
    const id = useMemo(() => _id || getUuid(), [_id]);
    const { fieldRef, dropdownRef, dropdownStyles } = useGetDropdownPosition({
        isOpen,
        maxWidth,
        resizeDeps,
        direction,
    });
    const handleClickOutside = useCallback(() => {
        if (typeof onClickOutside === 'function') {
            onClickOutside(setIsOpen);
        }
        else {
            setIsOpen(false);
        }
    }, [onClickOutside]);
    useEffect(() => {
        if (!isOpen)
            return;
        const onKeydown = (e) => {
            if (e.key === 'Escape') {
                handleClickOutside();
            }
        };
        document.addEventListener('keydown', onKeydown);
        return () => document.removeEventListener('keydown', onKeydown);
    }, [isOpen, handleClickOutside]);
    function handleOpen() {
        setIsOpen(true);
    }
    function handleClose() {
        setIsOpen(false);
    }
    function handleToggle() {
        setIsOpen((x) => !x);
    }
    useImperativeHandle(forwardedRef, () => ({
        fieldRef,
        isOpen: isOpen,
        onOpen: handleOpen,
        onClose: handleClose,
        onToggle: handleToggle,
        setIsOpen: setIsOpen,
    }));
    useEffect(() => {
        if (onOpen && isOpen === true) {
            onOpen();
        }
        if (onAfterClose && isOpen === false) {
            onAfterClose();
        }
    }, [isOpen]);
    return (_jsx(DropdownContext.Provider, Object.assign({ value: {
            fieldRef,
            isOpen,
            onOpen: handleOpen,
            onClose: handleClose,
            onToggle: handleToggle,
        } }, { children: _jsxs(ClickOutside, Object.assign({ id: id, className: classNames(styles.dropdown, className), onClick: handleClickOutside, contentEditable: false }, rest, { children: [renderButton({
                    fieldRef,
                    isOpen,
                    onOpen: handleOpen,
                    onClose: handleClose,
                    onToggle: handleToggle,
                    setIsOpen,
                }), isOpen && (_jsx("div", Object.assign({ className: classNames(styles.popup, classNamePopup), style: Object.assign(Object.assign({}, dropdownStyles), { maxHeight }), ref: dropdownRef, "data-dropdown-popup": true }, { children: children })))] })) })));
});
